import * as React from 'react';
import {FiChevronDown} from 'react-icons/fi';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import clsx from 'clsx';

import type {ArticleDetails} from '@/modules/article/shared/types';
import {Container} from '@/modules/foundation/components/container';
import {cn} from '@/modules/foundation/shared/tailwind';
import {NavArticleCard} from '@/modules/site/components/NavArticleCard';
import {NavLink} from '@/modules/site/components/NavLink';

const NavigationMenu = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({className, children, ...props}, ref) => (
	<NavigationMenuPrimitive.Root
		asChild
		ref={ref}
		className={cn(
			['z-10', 'flex', 'flex-1', 'items-center', 'justify-center'],
			className,
		)}
		aria-label=""
		{...props}
	>
		<div>
			{children}
			<NavigationMenuViewport />
		</div>
	</NavigationMenuPrimitive.Root>
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuList = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({className, ...props}, ref) => (
	<NavigationMenuPrimitive.List
		ref={ref}
		className={cn(
			[
				'group',
				'flex',
				'flex-1',
				'list-none',
				'items-end',
				'justify-end',
				'gap-10',
			],
			className,
		)}
		{...props}
	/>
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

interface NavigationMenuTriggerProps extends React.ComponentPropsWithoutRef<
	typeof NavigationMenuPrimitive.Trigger
> {
	activePath: boolean;
}

const NavigationMenuTrigger = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
	NavigationMenuTriggerProps
>(({className, children, activePath, ...props}, ref) => {
	return (
		<NavigationMenuPrimitive.Trigger
			ref={ref}
			className={cn(
				[
					'relative',
					'group',
					'hover:text-primary-15',
					'data-[state=open]:text-primary-15',
					'inline-flex',
					'w-max',
					'items-center',
					'justify-center',
					'rounded-md',
					'text-lg',
					'transition-colors',
					'focus:outline-hidden',
					'disabled:pointer-events-none',
					'disabled:opacity-50',
					'leading-none',
					'font-display',
					activePath && [
						'after:absolute',
						'after:h-[2px]',
						'after:-bottom-2',
						'after:w-full',
						'after:bg-primary-5',
						// 'data-[state=open]:after:h-0',
						// "hover:after:h-0",
					],
				],
				className,
			)}
			onPointerMove={(event) => event.preventDefault()}
			onPointerLeave={(event) => event.preventDefault()}
			{...props}
		>
			{children}{' '}
			<FiChevronDown
				className={clsx(
					'ml-1',
					'block',
					'transition',
					'duration-300',
					'text-lg',
					'group-data-[state=open]:rotate-180',
					'group-data-[state=open]:text-primary-15',
					'group-hover:text-primary-15',
					'text-secondary-40',
				)}
				aria-hidden="true"
			/>
		</NavigationMenuPrimitive.Trigger>
	);
});
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({className, ...props}, ref) => (
	<NavigationMenuPrimitive.Content
		ref={ref}
		className={cn(
			[
				'left-0',
				'top-0',
				'right-0',
				'data-[motion=from-end]:animate-enterFromRight',
				'data-[motion=from-start]:animate-enterFromLeft',
				'data-[motion=to-end]:animate-exitToRight',
				'data-[motion=to-start]:animate-exitToLeft',
				'grid',
				'grid-cols-12',
				'gap-10',
				'w-full',
				'pt-8',
				'pb-14',
			],
			className,
		)}
		onPointerEnter={(event) => event.preventDefault()}
		onPointerLeave={(event) => event.preventDefault()}
		{...props}
	/>
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuViewport = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
	React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({className, ...props}, ref) => (
	<div
		className={cn([
			'absolute',
			'left-0',
			'right-0',
			'top-[calc(100%+1px)]',
			'bg-white',
			'shadow-[0px_100px_100px_0px_rgba(18,25,30,0.10),0px_6px_6px_0px_rgba(18,25,30,0.15)]',
		])}
	>
		<Container>
			<NavigationMenuPrimitive.Viewport
				className={cn(
					[
						'relative',
						// 'mt-2.5',
						'h-(--radix-navigation-menu-viewport-height)',
						'w-full',
						'origin-[top_center]',
						'overflow-hidden',
						'rounded-md',
						'transition-[width,height]',
						'duration-300',
						// 'data-[state=closed]:animate-scaleOut',
						'data-[state=open]:animate-scaleIn',
						// 'sm:w-(--radix-navigation-menu-viewport-width)',
					],
					className,
				)}
				ref={ref}
				{...props}
			/>
		</Container>
	</div>
));
NavigationMenuViewport.displayName =
	NavigationMenuPrimitive.Viewport.displayName;

const NavigationMenuIndicator = React.forwardRef<
	React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
	React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({className, ...props}, ref) => (
	<NavigationMenuPrimitive.Indicator
		ref={ref}
		className={cn(
			[
				'top-full',
				'z-1',
				'flex',
				'h-1.5',
				'items-end',
				'justify-center',
				'overflow-hidden',
				'transition-[width,transform_250ms_ease]',
				'data-[state=hidden]:animate-fadeOut',
				'data-[state=visible]:animate-fadeIn',
			],
			className,
		)}
		{...props}
	>
		<div
			className={clsx(
				'bg-border',
				'relative',
				'top-[60%]',
				'size-2',
				'rotate-45',
				'rounded-tl-sm',
				'shadow-md',
			)}
		/>
	</NavigationMenuPrimitive.Indicator>
));
NavigationMenuIndicator.displayName =
	NavigationMenuPrimitive.Indicator.displayName;

const NavigationMenuContentGroup = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	return <div className={clsx('col-span-3')}>{children}</div>;
};

const NavigationMenuContentGroupList = ({
	children,
	title,
	className,
	hideTitle,
}: {
	children: React.ReactNode;
	title: string;
	className?: string;
	hideTitle?: boolean;
}) => {
	const id = React.useId();
	return (
		<>
			<strong
				id={id}
				className={clsx(
					hideTitle && 'sr-only',
					'text-grey-15',
					'font-display',
					'font-medium',
					'mb-3',
					'inline-block',
				)}
			>
				{title}
			</strong>
			<ul
				aria-labelledby={id}
				className={cn(['grid', 'gap-3'], className)}
			>
				{children}
			</ul>
		</>
	);
};

const NavigationMenuContentGroupListItem = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	return <li>{children}</li>;
};

export const NavigationMenuArticleLink = ({
	article,
	idx,
}: {
	article: ArticleDetails;
	idx: number;
}) => {
	return (
		<NavigationMenuPrimitive.Link asChild>
			<NavArticleCard
				image={idx === 0 ? article.imageWeb : undefined}
				title={article.title}
				description={article.description}
				href={article.href}
				className={clsx(
					idx !== 0 && ['border-t', 'border-grey-border', 'pt-4'],
				)}
			/>
		</NavigationMenuPrimitive.Link>
	);
};

const NavigationMenuLink = ({
	children,
	href,
	className,
	featured,
}: {
	children: React.ReactNode;
	href: string;
	className?: string;
	featured?: boolean;
}) => {
	return (
		<NavigationMenuPrimitive.Link asChild>
			<NavLink featured={featured} href={href} className={cn(className)}>
				{children}
			</NavLink>
		</NavigationMenuPrimitive.Link>
	);
};

export {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuContentGroup,
	NavigationMenuContentGroupList,
	NavigationMenuContentGroupListItem,
	NavigationMenuIndicator,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationMenuViewport,
};
